import { T } from '@tolgee/react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';

const US_FORM_URL = 'https://forms.gle/1aQGQhfPe9nqVNbV8';
const INTERNATIONAL_FORM_URL = 'https://forms.gle/orySfPooDHA47qHo9';

const CROWN_SRC = '/images/school-and-free-plan/crown-filled.svg';
const MIXED_SRC = '/images/school-and-free-plan/mixed-plan-skills.png';

const SchoolAndFreePlanContent = () => {
  const { accessBadge } = useAccessBadge();
  // Only confirmed US orgs get the US form; everything else (signed-out
  // visitors, badge still loading, badges without a country) defaults to the
  // international form.
  const country = accessBadge?.organization?.country;
  const isUS = !!country && country.toUpperCase() === 'US';
  const formUrl = isUS ? US_FORM_URL : INTERNATIONAL_FORM_URL;

  return (
    <Page>
      <Hero>
        <HeroKicker>
          <T
            keyName="SchoolAndFreePlan.Hero.Kicker"
            defaultValue="How the Curipod free plan works"
          />
        </HeroKicker>
        <HeroTitle>
          <T keyName="SchoolAndFreePlan.Hero.Title" defaultValue="Free vs School Plan" />
        </HeroTitle>
        <HeroLede>
          <T
            keyName="SchoolAndFreePlan.Hero.Lede"
            defaultValue="This page walks through what you get each week on the free plan, how the free School Plan Trials work, and how we can help you get unlimited access."
          />
        </HeroLede>
      </Hero>

      <Section>
        <SectionHead>
          <Eyebrow $variant="nudge">
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Eyebrow"
              defaultValue="The free plan"
            />
          </Eyebrow>
          <SectionTitle>
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Title"
              defaultValue="Teaching Sessions Limit"
            />
          </SectionTitle>
          <Lede>
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.IntroFree"
              defaultValue="Any lesson that isn't marked with an orange crown is free for everyone — you can always use these lessons and activities, but there is a limit on the number of teaching sessions."
            />{' '}
            <T keyName="SchoolAndFreePlan.TeachingSessions.Intro3" defaultValue="A" />{' '}
            <strong>
              <T
                keyName="SchoolAndFreePlan.TeachingSessions.TeachingSessionTerm"
                defaultValue="Teaching Session"
              />
            </strong>{' '}
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Intro4"
              defaultValue="starts when you click"
            />{' '}
            <em>
              <T
                keyName="SchoolAndFreePlan.TeachingSessions.StartLesson"
                defaultValue="Start Lesson"
              />
            </em>{' '}
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Intro5"
              defaultValue="and students join using the join code."
            />{' '}
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Intro6"
              defaultValue="You get"
            />{' '}
            <strong>
              <T
                keyName="SchoolAndFreePlan.TeachingSessions.TwoPerWeek"
                defaultValue="2 sessions every week"
              />
            </strong>{' '}
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Intro7"
              defaultValue="on the free plan. Using the same lesson across multiple classes in one week counts as only one session."
            />
            <br />
            <br />
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.Example"
              defaultValue="Example: If you use a Quick Write lesson with your 5 ELA classes in one week, it only counts as one session."
            />
          </Lede>
        </SectionHead>

        <Card>
          <CardHead>
            <div>
              <CardTitle>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Card.Title"
                  defaultValue="What you get, free, every week"
                />
              </CardTitle>
              <CardLede>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Card.Lede"
                  defaultValue="More than enough to try Curipod with your class, build a weekly ritual, or run a warm-up on Fridays."
                />
              </CardLede>
            </div>
          </CardHead>

          <StatRow>
            <StatNum $variant="nudge">2</StatNum>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat1.Label"
                  defaultValue="Teaching Sessions per week"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat1.Desc.1"
                  defaultValue="Teach the"
                />{' '}
                <strong>
                  <T
                    keyName="SchoolAndFreePlan.TeachingSessions.Stat1.Desc.Bold"
                    defaultValue="same lesson"
                  />
                </strong>{' '}
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat1.Desc.2"
                  defaultValue="with as many classes as you want in a week — it still only counts as one session."
                />
              </StatDesc>
            </div>
          </StatRow>

          <StatRow>
            <StatNum $variant="nudge">∞</StatNum>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat2.Label"
                  defaultValue="Lesson creation & editing"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat2.Desc"
                  defaultValue="Build, remix, and save lessons without limits. The cap is only on using the lesson in the classroom."
                />
              </StatDesc>
            </div>
          </StatRow>

          <StatRow>
            <StatIcon $variant="nudge">
              <RefreshSvg />
            </StatIcon>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat3.Label"
                  defaultValue="Renews automatically, every Sunday"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.TeachingSessions.Stat3.Desc"
                  defaultValue="Your two sessions reset at the start of the week."
                />
              </StatDesc>
            </div>
          </StatRow>
        </Card>

        <BonusNote>
          <BonusIcon>
            <StarSvg />
          </BonusIcon>
          <p>
            <strong>
              <T
                keyName="SchoolAndFreePlan.TeachingSessions.BonusBold"
                defaultValue="New to Curipod?"
              />
            </strong>{' '}
            <T
              keyName="SchoolAndFreePlan.TeachingSessions.BonusText"
              defaultValue="New teachers get additional bonus Teaching Sessions to help you get started."
            />
          </p>
        </BonusNote>
      </Section>

      <Section>
        <SectionHead>
          <Eyebrow $variant="orange">
            <EyebrowIcon src={CROWN_SRC} alt="" />
            <T
              keyName="SchoolAndFreePlan.Trials.Eyebrow"
              defaultValue="The School Plan"
            />
          </Eyebrow>
          <SectionTitle>
            <T
              keyName="SchoolAndFreePlan.Trials.Title"
              defaultValue="School Plan Trials"
            />
          </SectionTitle>
          <Lede>
            <T
              keyName="SchoolAndFreePlan.Trials.Intro1"
              defaultValue="Some Curipod lessons or skills are marked"
            />{' '}
            <em>
              <T
                keyName="SchoolAndFreePlan.Trials.SchoolPlanTerm"
                defaultValue="School Plan,"
              />{' '}
              <span style={{ fontWeight: 'normal' }}>
                <T
                  keyName="SchoolAndFreePlan.Trials.Intro2"
                  defaultValue="these are marked with an orange crown icon"
                />{' '}
                <InlineIcon src={CROWN_SRC} alt="crown" />
              </span>
            </em>
            .{' '}
            <T
              keyName="SchoolAndFreePlan.Trials.Intro3"
              defaultValue="Every teacher gets"
            />{' '}
            <strong>
              <T
                keyName="SchoolAndFreePlan.Trials.ThreeFreeTrials"
                defaultValue="three free trials"
              />
            </strong>{' '}
            <T
              keyName="SchoolAndFreePlan.Trials.Intro4"
              defaultValue="of these lessons so you can try out the School Plan."
            />
          </Lede>
        </SectionHead>

        <Card>
          <CardHead>
            <Sticker $variant="orange">
              <img src={CROWN_SRC} alt="" />
            </Sticker>
            <div>
              <CardTitle>
                <T
                  keyName="SchoolAndFreePlan.Trials.Card.Title"
                  defaultValue="Three trials"
                />
              </CardTitle>
              <CardLede>
                <T
                  keyName="SchoolAndFreePlan.Trials.Card.Lede.1"
                  defaultValue="Unlike Teaching Sessions, these"
                />{' '}
                <strong>
                  <T
                    keyName="SchoolAndFreePlan.Trials.Card.Lede.Bold"
                    defaultValue="do not"
                  />
                </strong>{' '}
                <T keyName="SchoolAndFreePlan.Trials.Card.Lede.2" defaultValue="renew." />
              </CardLede>
            </div>
          </CardHead>

          <StatRow>
            <StatNum>3</StatNum>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat1.Label"
                  defaultValue="Total School Plan Trials"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat1.Desc"
                  defaultValue="Counted once, across your whole account — not per lesson, not per week."
                />
              </StatDesc>
            </div>
          </StatRow>

          <StatRow>
            <StatIcon>
              <ClockSvg />
            </StatIcon>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat2.Label"
                  defaultValue="No weekly reset"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat2.Desc"
                  defaultValue="Once you've used all three you can't continue to use them."
                />
              </StatDesc>
            </div>
          </StatRow>

          <StatRow>
            <StatIcon>
              <LockSvg />
            </StatIcon>
            <div>
              <StatLabel>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat3.Label"
                  defaultValue="Independent of the 2/week"
                />
              </StatLabel>
              <StatDesc>
                <T
                  keyName="SchoolAndFreePlan.Trials.Stat3.Desc"
                  defaultValue="Running a School Plan Trial still uses one of your two weekly Teaching Sessions — it's both a trial and a live lesson."
                />
              </StatDesc>
            </div>
          </StatRow>
        </Card>

        <Card>
          <CardHead>
            <Sticker $variant="orange">
              <img src={CROWN_SRC} alt="" />
            </Sticker>
            <div>
              <CardTitle>
                <T
                  keyName="SchoolAndFreePlan.Trials.Mixed.Title"
                  defaultValue="Some School Plan activities have sections available for free"
                />
              </CardTitle>
              <CardLede>
                <T
                  keyName="SchoolAndFreePlan.Trials.Mixed.LedeCrownOnly"
                  defaultValue="Some activities marked with the orange crown"
                />{' '}
                <InlineIcon src={CROWN_SRC} alt="" />{' '}
                <T
                  keyName="SchoolAndFreePlan.Trials.Mixed.Lede.4"
                  defaultValue="have some skills or genres available for free, while some are only on the school plan. When you open one of these activities, you'll clearly see which parts are available for free as in the example below."
                />
              </CardLede>
            </div>
          </CardHead>

          <ModalPlaceholder>
            <ModalImage
              src={MIXED_SRC}
              alt="Activity skill picker showing Free Plan and School Plan sections"
            />
          </ModalPlaceholder>
        </Card>
      </Section>

      <Section>
        <SectionHead>
          <Eyebrow $variant="leaf">
            <T
              keyName="SchoolAndFreePlan.GetPlan.Eyebrow"
              defaultValue="Moving your school to the plan"
            />
          </Eyebrow>
          <SectionTitle>
            <T
              keyName="SchoolAndFreePlan.GetPlan.Title"
              defaultValue="How to get Curipod School Plan."
            />
          </SectionTitle>
          <Lede>
            <T
              keyName="SchoolAndFreePlan.GetPlan.Lede"
              defaultValue='We know "talk to your principal" isn&apos;t a plan. So we have a full team to help teachers bring Curipod to their school.'
            />
          </Lede>
        </SectionHead>

        <Steps>
          <StepCard>
            <StepNum>1</StepNum>
            <StepTitle>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step1.Title"
                defaultValue="Tell us you're interested"
              />
            </StepTitle>
            <StepDesc>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step1.Desc"
                defaultValue="Fill out the short form (2 minutes). We will reach out shortly with a plan and help you get access."
              />
            </StepDesc>
            <StepLink href={formUrl} target="_blank" rel="noopener noreferrer">
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step1.Button"
                defaultValue="Fill out the form"
              />
              <ArrowRightSvg />
            </StepLink>
          </StepCard>

          <StepCard>
            <StepNum>2</StepNum>
            <StepTitle>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step2.Title"
                defaultValue="Invite your principal to observe a lesson"
              />
            </StepTitle>
            <StepDesc>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step2.Desc"
                defaultValue="We help you pick the lesson, schedule the walkthrough, and prep talking points. Your classroom is the pitch — it's what gets schools to yes."
              />
            </StepDesc>
          </StepCard>

          <StepCard>
            <StepNum>3</StepNum>
            <StepTitle>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step3.Title"
                defaultValue="We follow up with your principal"
              />
            </StepTitle>
            <StepDesc>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Step3.Desc"
                defaultValue="We can show how neighboring schools have funded and implemented Curipod. We can help find grants if there is no more budget."
              />
            </StepDesc>
          </StepCard>
        </Steps>

        <CtaPanel>
          <div>
            <CtaTitle>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Cta.Title"
                defaultValue="Let us help you get unlimited access!"
              />
            </CtaTitle>
            <CtaText>
              <T
                keyName="SchoolAndFreePlan.GetPlan.Cta.Text"
                defaultValue="Tell us a little about your school — we'll take it from there."
              />
            </CtaText>
          </div>
          <CtaButton href={formUrl} target="_blank" rel="noopener noreferrer">
            <T
              keyName="SchoolAndFreePlan.GetPlan.Cta.Button"
              defaultValue="Fill out the form"
            />
            <ArrowRightSvg />
          </CtaButton>
        </CtaPanel>
      </Section>
    </Page>
  );
};

export default SchoolAndFreePlanContent;

const LIGHT_ORANGE_BG = theme.colors.lightOrangeBg;
const SHADOW_ROW = '0 2px 20px 0 #deaf8d33';
const SHADOW_FLOAT =
  '0 1.6px 3.6px rgba(0, 0, 0, 0.13), 0 0.3px 0.9px rgba(0, 0, 0, 0.11)';
const SHADOW_MAGIC =
  '0 2.6px 5.6px rgba(0, 0, 0, 0.13), 0 0.9px 1.3px rgba(0, 0, 0, 0.11)';
const SHADOW_MAGIC_HOVER =
  '0 10.6px 15px rgba(0, 0, 0, 0.13), 0 10.3px 20px rgba(0, 0, 0, 0.11)';
const TRANSITION = 'all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)';

const Page = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px 28px 96px;
  color: ${theme.colors.black};
  background: transparent;

  @media (max-width: 600px) {
    padding: 24px 16px 64px;
  }
`;

const Hero = styled.header`
  background: ${theme.colors.white};
  border-radius: 15px;
  padding: 44px 48px 40px;
  box-shadow: ${SHADOW_ROW};
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 260px;
    height: 260px;
    background: radial-gradient(
      circle,
      ${theme.colors.translucentOrange} 0%,
      transparent 70%
    );
    opacity: 0.35;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    padding: 32px 24px 28px;
    border-radius: 10px;
  }
`;

const HeroKicker = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  letter-spacing: 0.14em;
  background: ${LIGHT_ORANGE_BG};
  padding: 6px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(28px, 5.2vw, 48px);
  font-weight: 900;
  line-height: 1.1;
  margin: 0 0 16px;
  max-width: 20ch;
  letter-spacing: -0.015em;
`;

const HeroLede = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${theme.colors.greyDark};
  font-weight: 400;
  max-width: 62ch;
  margin: 0;
`;

const Section = styled.section`
  padding: 32px 0;
  scroll-margin-top: 32px;
`;

const SectionHead = styled.div`
  margin-bottom: 24px;
`;

const Eyebrow = styled.span<{ $variant: 'orange' | 'nudge' | 'leaf' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 20px;
  margin-bottom: 14px;

  ${({ $variant }) =>
    $variant === 'orange' &&
    `
      background: ${LIGHT_ORANGE_BG};
      color: ${theme.colors.orangeDark};
    `}
  ${({ $variant }) =>
    $variant === 'nudge' &&
    `
      background: ${theme.colors.schoolPlanNudgeBg};
      color: ${theme.colors.schoolPlanNudgeIcon};
      border: 1px solid ${theme.colors.schoolPlanNudgeBorder};
    `}
  ${({ $variant }) =>
    $variant === 'leaf' &&
    `
      background: ${theme.colors.successBg};
      color: ${theme.colors.successText};
      border: 1px solid ${theme.colors.successBorder};
    `}
`;

const EyebrowIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const InlineIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: inline-block;
  vertical-align: -5px;
  margin: 0 2px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3.2vw, 34px);
  font-weight: 900;
  line-height: 1.25;
  letter-spacing: -0.01em;
  margin: 0 0 10px;
`;

const Lede = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${theme.colors.greyDark};
  font-weight: 400;
  margin: 0;

  strong {
    font-weight: 700;
    color: ${theme.colors.black};
  }

  em {
    font-style: italic;
    font-weight: 700;
  }
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: 15px;
  padding: 28px 30px 26px;
  box-shadow: ${SHADOW_ROW};
  position: relative;

  & + & {
    margin-top: 24px;
  }

  @media (max-width: 600px) {
    padding: 22px 20px;
    border-radius: 10px;
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
`;

const Sticker = styled.div<{ $variant?: 'orange' | 'nudge' }>`
  flex: none;
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background: ${theme.colors.landingPageBeige};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;

  ${({ $variant }) => $variant === 'orange' && `background: ${LIGHT_ORANGE_BG};`}
  ${({ $variant }) =>
    $variant === 'nudge' && `background: ${theme.colors.schoolPlanNudgeBg};`}

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CardTitle = styled.h3`
  font-size: 22px;
  font-weight: 900;
  margin: 4px 0 4px;
  line-height: 1.2;
`;

const CardLede = styled.p`
  font-size: 16px;
  color: ${theme.colors.greyDark};
  font-weight: 400;
  line-height: 1.45;
  margin: 0;

  strong {
    font-weight: 700;
    color: ${theme.colors.black};
  }
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 0;
  border-top: 1px dashed ${theme.colors.beigeDark};

  &:first-of-type {
    border-top: none;
    padding-top: 8px;
  }
`;

const StatNum = styled.div<{ $variant?: 'nudge' }>`
  flex: none;
  font-weight: 900;
  font-size: 40px;
  line-height: 1;
  color: ${({ $variant }) =>
    $variant === 'nudge' ? theme.colors.schoolPlanNudgeIcon : theme.colors.orange};
  width: 54px;
  text-align: center;
  letter-spacing: -0.02em;
`;

const StatIcon = styled.div<{ $variant?: 'nudge' }>`
  flex: none;
  width: 54px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $variant }) =>
    $variant === 'nudge' ? theme.colors.schoolPlanNudgeIcon : theme.colors.orange};
`;

const StatLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 2px;
  line-height: 1.3;
`;

const StatDesc = styled.p`
  font-size: 14px;
  color: ${theme.colors.greyDark};
  font-weight: 400;
  line-height: 1.45;
  margin: 0;

  strong {
    font-weight: 700;
    color: ${theme.colors.black};
  }
`;

const BonusNote = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 18px 22px;
  background: ${theme.colors.schoolPlanNudgeBg};
  border: 1px solid ${theme.colors.schoolPlanNudgeBorder};
  border-radius: 10px;
  color: ${theme.colors.schoolPlanNudgeIcon};

  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.45;
    color: ${theme.colors.schoolPlanNudgeIcon};
    font-weight: 400;
  }

  p strong {
    font-weight: 900;
  }
`;

const BonusIcon = styled.div`
  flex: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  background: ${theme.colors.white};
  color: ${theme.colors.schoolPlanNudgeIcon};
`;

const ModalPlaceholder = styled.div`
  margin-top: 20px;
  background: ${theme.colors.landingPageBeige};
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: ${SHADOW_FLOAT};
  display: block;
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 15px;
  padding: 26px 24px 22px;
  box-shadow: ${SHADOW_ROW};
  position: relative;

  @media (max-width: 600px) {
    padding: 22px 20px;
    border-radius: 10px;
  }
`;

const StepNum = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 900;
  margin-bottom: 14px;
  box-shadow: ${SHADOW_FLOAT};
`;

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 900;
  line-height: 1.3;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
`;

const StepDesc = styled.p`
  font-size: 14px;
  color: ${theme.colors.greyDark};
  font-weight: 400;
  line-height: 1.45;
  margin: 0;
`;

const StepLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.08), ${SHADOW_FLOAT};
  transition: ${TRANSITION};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 0 rgba(0, 0, 0, 0.08), ${SHADOW_MAGIC_HOVER};
    color: ${theme.colors.white};
  }
`;

const CtaPanel = styled.div`
  background: ${theme.colors.orangeGradient};
  color: ${theme.colors.white};
  border-radius: 15px;
  padding: 36px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  flex-wrap: wrap;
  margin-top: 32px;
  box-shadow: ${SHADOW_MAGIC};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    right: -60px;
    bottom: -60px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: 600px) {
    padding: 28px 24px;
    border-radius: 10px;
  }
`;

const CtaTitle = styled.h3`
  font-size: 24px;
  font-weight: 900;
  margin: 0 0 6px;
  letter-spacing: -0.01em;
  color: ${theme.colors.white};
`;

const CtaText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.45;
  max-width: 48ch;
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  text-decoration: none;
  border: none;
  border-radius: 10px;
  padding: 14px 22px;
  font-size: 16px;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
  transition: ${TRANSITION};
  font-family: inherit;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.08), ${SHADOW_FLOAT};
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 0 rgba(0, 0, 0, 0.08), ${SHADOW_MAGIC_HOVER};
    color: ${theme.colors.orangeDark};
  }
`;

function RefreshSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="30"
      height="30"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

function ClockSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4l2 2" />
    </svg>
  );
}

function LockSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function StarSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2l2.4 5 5.6.8-4 4 1 5.7L12 15l-5 2.5 1-5.7-4-4 5.6-.8L12 2z" />
    </svg>
  );
}

function ArrowRightSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
